import { AxiosError, AxiosResponse } from 'axios';
import { reactive, UnwrapNestedRefs } from 'vue';
import PubSub from 'pubsub-js';
import { debounce } from 'underscore';
import type {
  DMPayinTransactionResponse,
  MoneyProcessingCallbackData,
} from '@nsftx/systems-sdk';
import { isDmApplicationRuntime } from '@/common/stores/environment-handler';
import { useNotificationsStore, TNotificationTypeEnum } from '@/common/stores/notifications';
import { errorParser } from '@/common/services/error-parser';
import { logService } from '@/common/services/logger';
import { useGravitySettingsStore } from '@/modules/cms/gravity-settings';
import i18n from '@/plugins/i18n';
import BaseError from '@/common/errors/BaseError';
import HooksManager from '@/common/services/HooksManager';
import { narApiService } from '@/modules/nar';
import {
  attachPocketListeners,
  initPocket,
  payinCancel,
  transfer,
  payinRetry,
} from '@/modules/device-management';
import { initDmMoney, getMoneyInstance } from './dm/dmAcceptorsService';
import * as narAcceptorsService from './nar/narAcceptorsService';
import {
  type AcceptorHookParam,
  type AcceptorsStatus,
  type AcceptorType,
  type MoneyNotificationEvent,
  type AcceptorHookErrorResponse,
  AcceptorAction,
  TerminalAcceptorTransaction,
  type AcceptorStateChangeEvent,
} from './types';
import * as apiService from './apiService';
import * as dmAcceptorsService from './dm/dmAcceptorsService';
import { handleAcceptorsChangeStateError, handleNarAcceptorsChangeStateSuccess } from './helpers';

const { t } = i18n.global;

const acceptors: UnwrapNestedRefs<AcceptorsStatus> = reactive({
  bill: {
    status: false,
  },
  coin: {
    status: false,
  },
});
const whitelistedStartAcceptorsErrorMessages: string[] = [
  'acceptors-start-error-tbo',
  'acceptors-start-error-device-cash-deposit',
  'acceptors-start-error-oktopay',
  'acceptors-start-error-working-time',
  'acceptors-start-error-paper-low',
  'acceptors-start-error-paper-out',
  'acceptors-start-error-printer-not-selected',
];

let acceptorProcessingAmount: boolean;
let isCheckingInProgress = true;
let areAcceptorsInitialized: boolean;
let countOfTries: number = 0;
let promisesArray: Array<{ resolve: Function, reject: Function, actionName: AcceptorAction }> = [];
let runPromisesTimeout: number;
let initStateTimeout: number;
let initialDetectionExecuted: boolean = false;

/**
 * Returns parsed acceptors errors as object with upstream_message and upstream_code,
 * where every code and message is divided with -
 * This is mostly for Graylog logs usage so we have clean error on the log.
 * @param {Array<AcceptorHookErrorResponse> | undefined} errors
 * @returns {Object}
 */
const parseAcceptorsHookError = (errors: Array<AcceptorHookErrorResponse> | undefined) => {
  const parsedErr = {
    upstream_code: '',
    upstream_message: '',
  };

  if (errors?.length) {
    errors.forEach((element) => {
      parsedErr.upstream_message += ` - ${element.message}`;
      parsedErr.upstream_code += ` - ${element.code}`;
    });
  }

  return parsedErr;
};

const executePromises = (): void => {
  const isAnyStopAttempt = !!promisesArray.find(
    (promise) => promise.actionName === AcceptorAction.STOP,
  );

  promisesArray.forEach((promise) => {
    const context = {
      areAcceptorsInitialized,
      isAnyStopAttempt,
      actionName: promise.actionName,
    };

    if (areAcceptorsInitialized) {
      if (isAnyStopAttempt) {
        if (promise.actionName === AcceptorAction.STOP) {
          promise.resolve();
        } else {
          promise.reject(new BaseError(
            'Cannot execute START action because there is STOP attempt by other call',
            'T_ACCEPTORS_INIT_ON_READY_REJECTED',
            {
              context,
            },
          ));
        }
      } else {
        promise.resolve();
      }
    } else {
      promise.reject(new BaseError(
        'Acceptors on ready promise rejected',
        'T_ACCEPTORS_INIT_ON_READY_REJECTED',
        {
          context,
        },
      ));
    }
  });

  promisesArray = [];
};

const validateStartAcceptors = (): Promise<void> => new Promise((resolve, reject) => {
  const params: AcceptorHookParam = {
    errorResponses: [],
  };
  HooksManager.run('BeforeAcceptorsStart', params).then(() => {
    if (params.errorResponses.length) {
      reject(params.errorResponses);
    } else {
      resolve();
    }
  }).catch((error) => {
    reject(error);
  });
});

const changeAcceptorsState = debounce((
  event: AcceptorStateChangeEvent,
  acceptMoney: boolean,
  options?: {
    isOperator?: boolean
  },
) => {
  const action = acceptMoney ? 'START' : 'STOP';

  if (isCheckingInProgress) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    waitAndRetryAcceptorsStateChange(event, acceptMoney);

    return;
  }

  if (!areAcceptorsInitialized) {
    const code = 'T_ACCEPTORS_NOT_INITIALIZED';
    const message = `[AcceptorsService] Acceptors are not initialized. Could not ${action} them`;

    logService.error(message, {
      code,
      details: {
        acceptMoney,
        isCheckingInProgress,
        areAcceptorsInitialized,
      },
    });
    event.reject(new BaseError(
      message,
      code,
      {
        context: {
          isCheckingInProgress,
          areAcceptorsInitialized,
        },
      },
    ));

    return;
  }

  if (isDmApplicationRuntime()) {
    dmAcceptorsService.changeAcceptorsStateOnInitialization(acceptMoney, event);
    return;
  }

  narAcceptorsService.changeAcceptorsState(acceptMoney, options?.isOperator)
    .then(() => {
      handleNarAcceptorsChangeStateSuccess(acceptMoney, event);
    }).catch((err) => {
      handleAcceptorsChangeStateError(acceptMoney, event, err);
    });
}, 2000);

const waitAndRetryAcceptorsStateChange = (
  event: AcceptorStateChangeEvent,
  acceptMoney: boolean,
) => {
  logService.debug('[AcceptorsService] Checking if acceptors are initialized. Waiting ...', {
    code: 'T_ACCEPTORS_INITIALIZED_WAITING',
    details: {
      acceptMoney,
    },
  });
  window.setTimeout(() => {
    changeAcceptorsState(event, acceptMoney);
  }, 1000);
};

/**
   * @description This method calls all registered promises (those who are waiting for
   * acceptors to be ready) after acceptors initialization check has been finished.
   * If acceptors are initialized and initial detection has been run, all promises will be
   * executed as resolved. Otherwise, all promises will be executed as rejected.
   */
const onInitializationFinished = (actionName: AcceptorAction): Promise<void> => {
  if (!isCheckingInProgress && initialDetectionExecuted) {
    if (areAcceptorsInitialized) {
      return Promise.resolve();
    }

    const error = new BaseError(
      'Cannot resolve ready call. Acceptors are not initialized.',
      'T_ACCEPTORS_NOT_INITIALIZED',
      {
        context: {
          areAcceptorsInitialized,
          isCheckingInProgress,
          actionName,
        },
      },
    );
    return Promise.reject(error);
  }

  const promise = new Promise<void>((resolve, reject) => {
    promisesArray.push({
      resolve,
      reject,
      actionName,
    });
  });

  return promise;
};

/**
   * @description Blocks/blaclists some error messages when
   * acceptors fail to start.
   */
// eslint-disable-next-line max-len
const filterStartAcceptorsErrorMessages = (unfilteredErrorMessages: Array<AcceptorHookErrorResponse>): Array<AcceptorHookErrorResponse> => unfilteredErrorMessages
  .filter((errorMessage) => whitelistedStartAcceptorsErrorMessages.includes(errorMessage.id || ''));

/**
   * @description Starts bill and coin acceptors.
   * Note: this method will be resolved only after acceptors
   * initialization check have been finished.
   */
const startAcceptors = (): Promise<void> => {
  const notificationsStore = useNotificationsStore();

  return new Promise((resolve, reject) => {
    validateStartAcceptors().then(() => {
      changeAcceptorsState({ resolve, reject }, true);
    }).catch((errResponse) => {
      const filteredErrorMessages = filterStartAcceptorsErrorMessages(errResponse);
      filteredErrorMessages.forEach((err: AcceptorHookErrorResponse) => {
        notificationsStore.show({
          id: err.id,
          message: err.message,
          type: TNotificationTypeEnum.error,
          delay: null,
        });
      });
      reject(errResponse);
    });
  });
};

/**
 * @description
 * This method will be resolved only after acceptors initilaization check have been finished.
 */
const stopAcceptors = (fromTbo?: boolean): Promise<void> => new Promise((resolve, reject) => {
  changeAcceptorsState({ resolve, reject }, false, { isOperator: fromTbo });
});

const detectInitialAcceptorsState = () => {
  if (!isCheckingInProgress) {
    executePromises();
    window.clearTimeout(runPromisesTimeout);
    initialDetectionExecuted = true;
  } else {
    runPromisesTimeout = window.setTimeout(detectInitialAcceptorsState, 1100);
  }
};

const markAcceptorsInitilizationAsFailed = (): void => {
  const notificationsStore = useNotificationsStore();
  isCheckingInProgress = false;
  areAcceptorsInitialized = false;
  countOfTries = 0;
  window.clearTimeout(initStateTimeout);
  executePromises();
  notificationsStore.show({
    message: t('acceptors_not_initialized'),
    type: TNotificationTypeEnum.error,
  });
};

const startAcceptorsInitializationStateChecker = (): void => {
  isCheckingInProgress = true;
  countOfTries += 1;

  if (countOfTries > 60) {
    logService.error('[AcceptorsService] Acceptors are not initialized.', {
      code: 'T_ACCEPTORS_INITIALIZATION_ERROR',
    });

    markAcceptorsInitilizationAsFailed();
    return;
  }

  if (isDmApplicationRuntime()) {
    Promise.all([
      initDmMoney(),
      initPocket(),
    ]).then(() => {
      isCheckingInProgress = false;
      window.clearTimeout(initStateTimeout);
      areAcceptorsInitialized = true;
      // attach listener now so we can listen for upcoming dm acceptor event changes
      dmAcceptorsService.registerDmAcceptorStartBlockersEvents();
      attachPocketListeners();

      onInitializationFinished(AcceptorAction.START).then(() => {
        logService.info('[acceptorsService] Starting acceptors after acceptors has been initialized.', {
          code: 'T_DM_MONEY_INIT_START',
        });
        startAcceptors().catch((error) => {
          // error comes in this shape
          // {message=Acceptors could not be started. Pass card is not scanned.,
          // id=acceptors-start-error-passcards, code=T_BEFORE_ACCEPTORS_START_PASS_CARDS_ERROR}
          const errorFormatted: {
            errorDetails: Object, code?: string
          } = {
            errorDetails: error,
            code: 'T_DM_MONEY_INIT_START_ERR',
          };

          logService.warn(
            '[AcceptorsService] Acceptors initialization - start acceptors error detected.',
            errorFormatted,
          );
        }).catch((err) => {
          logService.warn(
            '[AcceptorsService] Acceptors initialization - start acceptors error detected.',
            {
              error: err,
              code: 'T_DM_MONEY_INITIALIZATION_FAILED',
            },
          );
        });
      }).catch((err) => {
        logService.error('[AcceptorsService] Acceptors initialization route fails.', {
          code: 'T_DM_MONEY_INITIALIZATION_FAILS',
          ...errorParser.parseUpstream(err),
        });
        markAcceptorsInitilizationAsFailed();
      });
    }).catch((err) => {
      logService.error('[AcceptorsService] Pocket initialization failed.', {
        code: 'T_DM_POCKET_INITIALIZATION_FAILS',
        ...errorParser.parseUpstream(err),
      });
    });
  } else {
    narApiService.getAcceptorsInitializationStatus().then((response: AxiosResponse<{
      initialized: boolean
    }>) => {
      logService.info('[AcceptorsService] Acceptors initialization check route finished.', {
        details: {
          resData: response.data,
        },
        code: 'T_ACCEPTORS_INITIALIZATION_CHECK_FINISHED',
      });
      areAcceptorsInitialized = response?.status === 200 && response?.data?.initialized;

      if (areAcceptorsInitialized) {
        isCheckingInProgress = false;
        window.clearTimeout(initStateTimeout);
        onInitializationFinished(AcceptorAction.START).then(() => {
          logService.info('[acceptorsService] Starting acceptors...', {
            code: 'T_DCD_START_ACCEPTORS_REQUEST',
            upstream_code: 'AcceptorsService',
          });
          startAcceptors().catch((error) => {
            // error comes in this shape
            // {message=Acceptors could not be started. Pass card is not scanned.,
            // id=acceptors-start-error-passcards, code=T_BEFORE_ACCEPTORS_START_PASS_CARDS_ERROR}
            const errorFormatted: {
              errorDetails: Object, code?: string
            } = {
              errorDetails: error,
              code: 'T_ACCEPTORS_START_FAILED_ON_INIT',
            };

            logService.warn(
              '[AcceptorsService] Acceptors initialization - start acceptors error detected.',
              errorFormatted,
            );
          });
        }).catch((err) => {
          logService.warn(
            '[AcceptorsService] Acceptors initialization - start acceptors error detected.',
            {
              error: err,
              code: 'T_ACCEPTORS_INITIALIZATION_FAILED',
            },
          );
        });
      } else {
        initStateTimeout = window.setTimeout(startAcceptorsInitializationStateChecker, 1000);
      }
    }).catch((err) => {
      logService.error('[AcceptorsService] Acceptors initialization route fails.', {
        code: 'T_ACCEPTORS_INITIALIZATION_ROUTE_FAILS',
        ...errorParser.parseUpstream(err),
      });
      markAcceptorsInitilizationAsFailed();
    });
  }
};

const stopAcceptor = (acceptorsToStop: Array<AcceptorType>, withInfo: boolean): Promise<any[]> => {
  const promises: any = [];
  const notificationsStore = useNotificationsStore();

  acceptorsToStop.forEach((acceptor: AcceptorType) => {
    promises.push(
      apiService.stopAcceptor(acceptor).then(
        (response) => {
          if (response.status === 200) {
            if (withInfo) {
              notificationsStore.show(
                {
                  message: t(`${acceptor}_acc_stop_success`),
                  type: TNotificationTypeEnum.info,
                  delay: 3000,
                },
              );
            }

            acceptors[acceptor].status = false;
            localStorage.setItem(`acceptors.${acceptor}`, '0');
          } else {
            notificationsStore.show(
              {
                message: t('notifications.default_error_message'),
                type: TNotificationTypeEnum.warning,
                delay: 3000,
              },
            );
          }

          Promise.resolve(response);
        },
      ).catch((error: AxiosError) => {
        Promise.resolve(error);
      }),
    );
  });

  return Promise.all(promises);
};

function getAcceptorStatus(acceptor: AcceptorType): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (isDmApplicationRuntime()) {
      getMoneyInstance().getAcceptorStatus(acceptor).then((response) => {
        acceptors[acceptor].status = response.status.started;
        resolve(acceptors[acceptor].status);
      }).catch((err) => {
        logService.warn('[AcceptorsService] Get acceptor status failed', {
          ...errorParser.parseUpstream(err),
          code: 'T_DM_GET_ACCEPTOR_STATUS_FAILED',
          acceptor,
        });
        acceptors[acceptor].status = false;
        return reject(false);
      });
    } else {
      apiService.getAcceptorStatus(acceptor).then(
        (response) => {
          acceptors[acceptor].status = Number(response.data.code) === 1;
          resolve(acceptors[acceptor].status);
        },
      ).catch((error: AxiosError) => {
        logService.warn('[AcceptorsService] Get acceptor status failed', {
          ...errorParser.parseUpstream(error),
          code: 'T_GET_ACCEPTOR_STATUS_FAILED',
          acceptor,
        });
        acceptors[acceptor].status = false;
        return reject(false);
      });
    }
  });
}

const showMoneyNotification = (
  event: MoneyNotificationEvent | MoneyProcessingCallbackData,
): void => {
  let amount: number | string;

  const isMoneyNotificationEvent = (
    e: MoneyNotificationEvent | MoneyProcessingCallbackData,
  ): e is MoneyNotificationEvent => (e as MoneyNotificationEvent)?.message?.amount !== undefined;

  if (isMoneyNotificationEvent(event)) {
    amount = event.message.amount;
  } else {
    amount = event.amount;
  }

  if (event.type === 'moneyProcessing') {
    if (acceptorProcessingAmount && amount) {
      PubSub.publish('Acceptor.Amount.Change', amount);
    }
  } else if (event.type === 'moneyProcessed') {
    // Add 0.5 second latency to overlap loaders just in case
    window.setTimeout(() => {
      if (acceptorProcessingAmount && amount) {
        PubSub.publish('Acceptor.Amount.Change', null);
      }
    }, 500);
  }
};

const init = () => {
  const gravitySettingsStore = useGravitySettingsStore();
  acceptorProcessingAmount = !!gravitySettingsStore.getModuleDataKeyValue('config', 'acceptorProcessingAmount');

  startAcceptorsInitializationStateChecker();
};

const cancelTransaction = async (id: string)
: Promise<TerminalAcceptorTransaction | DMPayinTransactionResponse> => {
  if (!isDmApplicationRuntime()) {
    return apiService.cancelTransaction(id);
  }

  return payinCancel(id);
};

const retryTransaction = async (id: string) => {
  if (!isDmApplicationRuntime()) {
    return apiService.retryTransaction(id);
  }

  return payinRetry(id);
};

const transferTerminalMoney = () => {
  if (!isDmApplicationRuntime()) {
    // notify acceptors
    return apiService.transfer('bill');
  }

  return transfer();
};

export {
  init,
  acceptors,
  stopAcceptor,
  getAcceptorStatus,
  showMoneyNotification,
  startAcceptors,
  stopAcceptors,
  onInitializationFinished,
  parseAcceptorsHookError,
  detectInitialAcceptorsState,
  validateStartAcceptors,
  cancelTransaction,
  retryTransaction,
  transferTerminalMoney,
};
